import { useEffect, useRef, useState, FormEvent } from "react";

export default function Contact() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setStatus("sending");
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      const res = await fetch("https://readdy.ai/api/form/d7lb2nj9e0v02gt2au00", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-28 md:py-36 bg-[#FAF7F2]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section label */}
        <div
          className={`flex items-center gap-4 mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <span
            className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            03 / Contact
          </span>
          <div className="flex-1 h-px bg-[#C9A961]/25" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left */}
          <div
            className={`transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <h2
              className="font-serif text-4xl md:text-5xl text-[#2B1810] leading-tight mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.01em" }}
            >
              Let&apos;s Connect
            </h2>
            <div className="w-16 h-px bg-[#C9A961] mb-8" />
            <p className="text-base text-[#2B1810]/70 leading-relaxed mb-10 max-w-md" style={{ fontFamily: "'Lato', sans-serif" }}>
              Whether you&apos;re curious about trade compliance, exploring a collaboration, or simply want to connect — I&apos;d love to hear from you. Every conversation starts with a question.
            </p>

            {/* Contact details */}
            <div className="space-y-5">
              <a
                href="mailto:msbeatricepate@gmail.com"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A961]/10 group-hover:bg-[#C9A961]/20 transition-colors duration-300">
                  <i className="ri-mail-line text-[#C9A961]" />
                </div>
                <span className="text-sm text-[#2B1810] group-hover:text-[#C9A961] transition-colors duration-300" style={{ fontFamily: "'Lato', sans-serif" }}>
                  msbeatricepate@gmail.com
                </span>
              </a>

              <a
                href="https://www.linkedin.com/in/beatricepate"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A961]/10 group-hover:bg-[#C9A961]/20 transition-colors duration-300">
                  <i className="ri-linkedin-line text-[#C9A961]" />
                </div>
                <span className="text-sm text-[#2B1810] group-hover:text-[#C9A961] transition-colors duration-300" style={{ fontFamily: "'Lato', sans-serif" }}>
                  LinkedIn Profile
                </span>
              </a>

              <a
                href="https://github.com/beatricepate"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A961]/10 group-hover:bg-[#C9A961]/20 transition-colors duration-300">
                  <i className="ri-github-line text-[#C9A961]" />
                </div>
                <span className="text-sm text-[#2B1810] group-hover:text-[#C9A961] transition-colors duration-300" style={{ fontFamily: "'Lato', sans-serif" }}>
                  GitHub
                </span>
              </a>
            </div>

            {/* Quote */}
            <div className="mt-12 p-6 border-l-2 border-[#C9A961] bg-white rounded-r-xl">
              <p className="text-base italic text-[#2B1810]/70 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px" }}>
                &ldquo;For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.&rdquo;
              </p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#C9A961] mt-3" style={{ fontFamily: "'Lato', sans-serif" }}>
                Jeremiah 29:11
              </p>
            </div>
          </div>

          {/* Right — Form */}
          <div
            className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C9A961]/15 mb-6">
                  <i className="ri-check-line text-[#C9A961] text-2xl" />
                </div>
                <h3
                  className="font-serif text-2xl text-[#2B1810] mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Message Sent
                </h3>
                <p className="text-sm text-[#2B1810]/60" style={{ fontFamily: "'Lato', sans-serif" }}>
                  Thank you for reaching out. I&apos;ll be in touch soon.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-6 text-[12px] uppercase tracking-widest text-[#C9A961] hover:text-[#2B1810] transition-colors duration-300 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-[#2B1810]/60 mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your full name"
                    className="w-full px-5 py-3.5 bg-white border border-[#2B1810]/15 rounded-lg text-sm text-[#2B1810] placeholder-[#B8A5A8] focus:outline-none focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961]/30 transition-all duration-300"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-[#2B1810]/60 mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full px-5 py-3.5 bg-white border border-[#2B1810]/15 rounded-lg text-sm text-[#2B1810] placeholder-[#B8A5A8] focus:outline-none focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961]/30 transition-all duration-300"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-[#2B1810]/60 mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    placeholder="What&apos;s this about?"
                    className="w-full px-5 py-3.5 bg-white border border-[#2B1810]/15 rounded-lg text-sm text-[#2B1810] placeholder-[#B8A5A8] focus:outline-none focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961]/30 transition-all duration-300"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-[#2B1810]/60" style={{ fontFamily: "'Lato', sans-serif" }}>
                      Message
                    </label>
                    <span className={`text-[11px] ${charCount > 450 ? "text-red-400" : "text-[#B8A5A8]"}`} style={{ fontFamily: "'Lato', sans-serif" }}>
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell me what&apos;s on your mind..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-5 py-3.5 bg-white border border-[#2B1810]/15 rounded-lg text-sm text-[#2B1810] placeholder-[#B8A5A8] focus:outline-none focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961]/30 transition-all duration-300 resize-none"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  />
                </div>

                {status === "error" && (
                  <p className="text-sm text-red-400" style={{ fontFamily: "'Lato', sans-serif" }}>
                    Something went wrong. Please try again or email directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full py-4 bg-[#2B1810] text-[#FAF7F2] text-[13px] uppercase tracking-widest rounded-full hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                >
                  {status === "sending" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
